import React, { useMemo } from "react";
import { get_latest_version_text } from "../../js/latest_version.js";

/**
 * @typedef {Object} NationalMarketUIProps
 * @property {Array<any>=} options
 * @property {boolean|null=} limitToCoreMarkets
 * @property {string|null=} selected
 * @property {boolean=} loading
 * @property {(market: string) => void=} onSelect
 * @property {() => void=} onConfirm
 */

const defaultMarketOptions = [
  {
    id: 1,
    market: "en",
    emoji: "🇺🇸",
    label: "United States",
    name: "English",
    description: "Smooth experience for global users",
    confirmText: "✔️ Continue in English",
  },
  {
    id: 16,
    market: "vi",
    emoji: "🇻🇳",
    label: "Vietnam",
    name: "Tiếng Việt",
    description: "Xây dựng phù hợp cho người dùng Việt Nam",
    confirmText: "✔️ Tiếp tục bằng tiếng Việt",
  },
  {
    id: 4,
    market: "hi",
    emoji: "🇮🇳",
    label: "India",
    name: "हिन्दी",
    description: "भारत के उपयोगकर्ताओं के लिए आसान अनुभव",
    confirmText: "✔️ हिन्दी में जारी रखें",
  },
  {
    id: 2,
    market: "zhs",
    emoji: "🇨🇳",
    label: "Mainland China",
    name: "简体中文",
    description: "适合中国大陆与新加坡用户",
    confirmText: "✔️ 继续（简体中文）",
  },
  {
    id: 3,
    market: "zht",
    emoji: "🇹🇼",
    label: "Taiwan",
    name: "繁體中文",
    description: "適合台灣、香港與澳門使用者",
    confirmText: "✔️ 繼續（繁體中文）",
  },
  {
    id: 5,
    market: "es",
    emoji: "🇪🇸",
    label: "Spanish",
    name: "Español",
    description: "Ideal para España y América Latina",
    confirmText: "✔️ Continuar en español",
  },
  {
    id: 6,
    market: "ar",
    emoji: "🇸🇦",
    label: "Arabic",
    name: "العربية",
    description: "واجهة مريحة للمستخدمين العرب",
    confirmText: "✔️ متابعة بالعربية",
  },
  {
    id: 7,
    market: "fr",
    emoji: "🇫🇷",
    label: "French",
    name: "Français",
    description: "Parfait pour les utilisateurs francophones",
    confirmText: "✔️ Continuer en français",
  },
  {
    id: 8,
    market: "bn",
    emoji: "🇧🇩",
    label: "Bangladesh",
    name: "বাংলা",
    description: "বাংলাভাষী ব্যবহারকারীদের জন্য সহজ অভিজ্ঞতা",
    confirmText: "✔️ বাংলা চালিয়ে যান",
  },
  {
    id: 9,
    market: "pt",
    emoji: "🇧🇷",
    label: "Brazil",
    name: "Português",
    description: "Ótimo para Brasil e países lusófonos",
    confirmText: "✔️ Continuar em português",
  },
  {
    id: 10,
    market: "ru",
    emoji: "🇷🇺",
    label: "Russia",
    name: "Русский",
    description: "Удобный интерфейс для русскоязычных пользователей",
    confirmText: "✔️ Продолжить на русском",
  },
  {
    id: 11,
    market: "ur",
    emoji: "🇵🇰",
    label: "Pakistan",
    name: "اردو",
    description: "اردو صارفین کے لیے آسان اور نرم انداز",
    confirmText: "✔️ اردو میں جاری رکھیں",
  },
  {
    id: 12,
    market: "id",
    emoji: "🇮🇩",
    label: "Indonesia",
    name: "Bahasa Indonesia",
    description: "Nyaman untuk pengguna Indonesia",
    confirmText: "✔️ Lanjut dalam Bahasa Indonesia",
  },
  {
    id: 13,
    market: "de",
    emoji: "🇩🇪",
    label: "Germany",
    name: "Deutsch",
    description: "Klar und angenehm für deutschsprachige Nutzer",
    confirmText: "✔️ Auf Deutsch fortfahren",
  },
  {
    id: 14,
    market: "ja",
    emoji: "🇯🇵",
    label: "Japan",
    name: "日本語",
    description: "日本のユーザー向けにわかりやすく最適化",
    confirmText: "✔️ 日本語で続ける",
  },
  {
    id: 15,
    market: "mr",
    emoji: "🇮🇳",
    label: "Marathi",
    name: "मराठी",
    description: "मराठी वापरकर्त्यांसाठी सोपी आणि आपुलकीची रचना",
    confirmText: "✔️ मराठीत पुढे जा",
  },
  {
    id: 17,
    market: "tr",
    emoji: "🇹🇷",
    label: "Türkiye",
    name: "Türkçe",
    description: "Türkçe konuşan kullanıcılar için akıcı deneyim",
    confirmText: "✔️ Türkçe devam et",
  },
  {
    id: 18,
    market: "sw",
    emoji: "🇹🇿",
    label: "Swahili",
    name: "Kiswahili",
    description: "Rahisi na rafiki kwa watumiaji wa Afrika Mashariki",
    confirmText: "✔️ Endelea kwa Kiswahili",
  },
  {
    id: 19,
    market: "ha",
    emoji: "🇳🇬",
    label: "Hausa",
    name: "Hausa",
    description: "Sauƙi kuma mai daɗi ga masu amfani da Hausa",
    confirmText: "✔️ Ci gaba da Hausa",
  },
  {
    id: 20,
    market: "fil",
    emoji: "🇵🇭",
    label: "Philippines",
    name: "Filipino",
    description: "Madaling gamitin para sa mga user sa Pilipinas",
    confirmText: "✔️ Magpatuloy sa Filipino",
  },
  {
    id: 21,
    market: "ko",
    emoji: "🇰🇷",
    label: "Korea",
    name: "한국어",
    description: "한국 사용자에게 익숙하고 편한 화면",
    confirmText: "✔️ 한국어로 계속",
  },
  {
    id: 22,
    market: "fa",
    emoji: "🇮🇷",
    label: "Persian",
    name: "فارسی",
    description: "برای کاربران فارسی‌زبان ساده و روان",
    confirmText: "✔️ ادامه با فارسی",
  },
  {
    id: 23,
    market: "pa",
    emoji: "🇮🇳",
    label: "Punjabi",
    name: "ਪੰਜਾਬੀ",
    description: "ਪੰਜਾਬੀ ਵਰਤੋਂਕਾਰਾਂ ਲਈ ਸੌਖਾ ਅਨੁਭਵ",
    confirmText: "✔️ ਪੰਜਾਬੀ ਵਿੱਚ ਜਾਰੀ ਰੱਖੋ",
  },
  {
    id: 24,
    market: "am",
    emoji: "🇪🇹",
    label: "Amharic",
    name: "አማርኛ",
    description: "ለአማርኛ ተጠቃሚዎች ቀላል እና ተስማሚ",
    confirmText: "✔️ በአማርኛ ቀጥል",
  },
  {
    id: 25,
    market: "th",
    emoji: "🇹🇭",
    label: "Thailand",
    name: "ไทย",
    description: "เหมาะกับผู้ใช้ชาวไทย ใช้งานง่าย",
    confirmText: "✔️ ดำเนินการต่อเป็นภาษาไทย",
  },
  {
    id: 26,
    market: "jv",
    emoji: "🇮🇩",
    label: "Javanese",
    name: "Basa Jawa",
    description: "Gampang lan trep kanggo pangguna Jawa",
    confirmText: "✔️ Terusake nganggo Basa Jawa",
  },
  {
    id: 27,
    market: "it",
    emoji: "🇮🇹",
    label: "Italy",
    name: "Italiano",
    description: "Esperienza naturale per utenti italiani",
    confirmText: "✔️ Continua in italiano",
  },
  {
    id: 28,
    market: "pcm",
    emoji: "🇳🇬",
    label: "Nigeria",
    name: "Nigerian Pidgin",
    description: "Simple and familiar for Naija users",
    confirmText: "✔️ Continue for Naija",
  },
  {
    id: 29,
    market: "ms",
    emoji: "🇲🇾",
    label: "Malay",
    name: "Bahasa Melayu",
    description: "Mesra dan mudah untuk pengguna Melayu",
    confirmText: "✔️ Teruskan dalam Bahasa Melayu",
  },
  {
    id: 30,
    market: "pl",
    emoji: "🇵🇱",
    label: "Poland",
    name: "Polski",
    description: "Przyjazny i prosty układ dla użytkowników z Polski",
    confirmText: "✔️ Kontynuuj po polsku",
  },
  {
    id: 31,
    market: "uk",
    emoji: "🇺🇦",
    label: "Ukraine",
    name: "Українська",
    description: "Зручно та зрозуміло для українських користувачів",
    confirmText: "✔️ Продовжити українською",
  },
  {
    id: 32,
    market: "nl",
    emoji: "🇳🇱",
    label: "Netherlands",
    name: "Nederlands",
    description: "Rustige en duidelijke ervaring voor Nederlandstaligen",
    confirmText: "✔️ Doorgaan in het Nederlands",
  },
  {
    id: 33,
    market: "ps",
    emoji: "🇦🇫",
    label: "Pashto",
    name: "پښتو",
    description: "د پښتو کاروونکو لپاره ساده او دوستانه بڼه",
    confirmText: "✔️ په پښتو دوام ورکړئ",
  },
  {
    id: 34,
    market: "ro",
    emoji: "🇷🇴",
    label: "Romania",
    name: "Română",
    description: "Interfață clară și prietenoasă pentru utilizatorii români",
    confirmText: "✔️ Continuă în română",
  },
  {
    id: 35,
    market: "my",
    emoji: "🇲🇲",
    label: "Myanmar",
    name: "မြန်မာ",
    description: "မြန်မာအသုံးပြုသူများအတွက် လွယ်ကူပြီး သက်တောင့်သက်သာ",
    confirmText: "✔️ မြန်မာဘာသာဖြင့် ဆက်လုပ်မည်",
  },
  {
    id: 36,
    market: "om",
    emoji: "🇪🇹",
    label: "Oromo",
    name: "Afaan Oromoo",
    description: "Fayyadamtoota Oromoof salphaa fi mijataa",
    confirmText: "✔️ Afaan Oromootiin itti fufi",
  },
  {
    id: 37,
    market: "so",
    emoji: "🇸🇴",
    label: "Somali",
    name: "Soomaali",
    description: "Khibrad fudud oo ku habboon dadka ku hadla Soomaaliga",
    confirmText: "✔️ Ku sii wad Soomaali",
  },
  {
    id: 38,
    market: "ne",
    emoji: "🇳🇵",
    label: "Nepali",
    name: "नेपाली",
    description: "नेपाली प्रयोगकर्ताका लागि सहज र आत्मीय",
    confirmText: "✔️ नेपालीमा जारी राख्नुहोस्",
  },
  {
    id: 39,
    market: "az",
    emoji: "🇦🇿",
    label: "Azerbaijani",
    name: "Azərbaycanca",
    description: "Azərbaycan dilli istifadəçilər üçün rahat seçim",
    confirmText: "✔️ Azərbaycanca davam et",
  },
  {
    id: 40,
    market: "mg",
    emoji: "🇲🇬",
    label: "Malagasy",
    name: "Malagasy",
    description: "Tsotra sy mahafinaritra ho an'ny mpampiasa Malagasy",
    confirmText: "✔️ Tohizo amin'ny teny Malagasy",
  },
];

function MarketOption({ item, selected, onSelect }) {
  const isSelected = selected == item.market;
  const flag = item?.emoji ? { emoji: item.emoji, label: item.label || item.market } : null;

  return (
    <div className="w-full flex flex-col items-start">
      <button
        type="button"
        onClick={() => onSelect(item.market)}
        className={`w-full flex items-center gap-2 px-4 py-2 rounded border-2 font-bold transition-colors duration-200 ${
          isSelected
            ? "border-black text-black bg-green-200"
            : "border-black text-black bg-white hover:bg-gray-100"
        }`}
      >
        {isSelected ? <span className="text-2xl leading-none">✓</span> : null}
        <span className="text-left">{item.name}</span>
        {flag ? (
          <span role="img" aria-label={flag.label} className="text-lg leading-none">
            {flag.emoji}
          </span>
        ) : null}
      </button>
      <p className="mt-1 text-sm text-gray-700">{item.description}</p>
    </div>
  );
}

/** @param {NationalMarketUIProps} props */
export default function NationalMarketUI({
  options = defaultMarketOptions,
  limitToCoreMarkets = null,
  selected,
  loading,
  onSelect,
  onConfirm,
}) {
  const resolvedOptions = Array.isArray(options) && options.length ? options : defaultMarketOptions;
  const latestVersionRaw =
    typeof document !== "undefined"
      ? (() => {
          const match = document.cookie
            .split("; ")
            .find((row) => row.startsWith("latest_version="));
          return match ? decodeURIComponent(match.split("=")[1]) : "";
        })()
      : "";

  const latestVersion = get_latest_version_text(latestVersionRaw);
  const cookieRequiresCoreMarketsOnly = latestVersion === "3";
  const effectiveLimitToCoreMarkets =
    typeof limitToCoreMarkets === "boolean"
      ? limitToCoreMarkets
      : cookieRequiresCoreMarketsOnly;

  const visibleOptions = useMemo(() => {
    if (!effectiveLimitToCoreMarkets) return resolvedOptions;
    return resolvedOptions.filter((item) => item.market === "en" || item.market === "vi");
  }, [resolvedOptions, effectiveLimitToCoreMarkets]);

  const selectedConfig = useMemo(
    () => visibleOptions.find((item) => item.market === selected) || null,
    [visibleOptions, selected]
  );

  return (
    <div className="flex items-center justify-center py-8 px-3">
      <div className="w-full max-w-4xl p-7 bg-white rounded-lg shadow-md text-center space-y-6">
        <h2 className="text-xl font-semibold text-slate-900">Select region 🌈</h2>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 w-full max-h-[460px] overflow-y-auto pr-1">
          {visibleOptions.map((item) => (
            <MarketOption
              key={item.id || item.market}
              item={item}
              selected={selected}
              onSelect={onSelect}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={onConfirm}
          disabled={!selectedConfig || loading}
          className={`mt-3 flex w-full bg-purple-400 hover:bg-purple-300 rounded-3xl px-8 py-2 mb-3 transition-colors duration-200 ${
            !selectedConfig || loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <div className="flex items-center justify-between flex-1">
            {loading ? <div className="spinner-border" role="status" /> : null}
            <span className="text-lg font-medium text-white">
              {selectedConfig?.confirmText || "Confirm"}
            </span>
            <div className="text-xl">{"\u27A4"}</div>
          </div>
        </button>
      </div>
    </div>
  );
}
